// src/pages/Productions/ProductionsPage.tsx
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clapperboard, Search } from 'lucide-react';
import StudioShell from '../../components/Studio/StudioShell';
import StudioHero from '../../components/Studio/StudioHero';
import ProductionCard from '../../components/Studio/ProductionCard';
import FilterChips from '../../components/Studio/FilterChips';
import {
  StudioEmptyState,
  StudioErrorState,
  StudioSkeleton,
} from '../../components/Studio/StudioStates';
import {
  CONTAINER,
  INPUT,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
} from '../../components/Studio/studioStyles';
import useStudioResource from '../../hooks/useStudioResource';
import useSEO from '../../hooks/useSEO';
import productionService from '../../services/productionService';

const STAGE_FILTERS = [
  { value: '', label: 'Everything' },
  { value: 'DEVELOPMENT', label: 'In development' },
  { value: 'PRE_PRODUCTION', label: 'Pre-production' },
  { value: 'IN_PRODUCTION', label: 'Shooting' },
  { value: 'POST_PRODUCTION', label: 'In post' },
  { value: 'RELEASED', label: 'Released' },
];

const PAGE_SIZE = 24;

const ProductionsPage = () => {
  useSEO({
    title: 'Productions — StrymHub Studios',
    description:
      'Every StrymHub Studios production, grouped by the film it belongs to rather than scattered across categories. Cast, crew and credits front and centre.',
    keywords:
      'strymhub productions, independent film productions, sheffield film production, indie short films, cast and crew credits',
    type: 'website',
  });

  const [stage, setStage] = useState('');
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [page, setPage] = useState(0);

  const productions = useStudioResource(
    (signal) =>
      productionService.getProductions(
        { stage: stage || undefined, search: submittedSearch || undefined, page, size: PAGE_SIZE },
        signal,
      ),
    [stage, submittedSearch, page],
  );

  const results = productions.data;
  const isFiltered = Boolean(stage || submittedSearch);

  const heroStats = useMemo(() => {
    if (!results) return undefined;
    return [{ label: 'Productions', value: String(results.totalElements) }];
  }, [results]);

  const handleStageChange = (value: string) => {
    setStage(value);
    setPage(0);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmittedSearch(search.trim());
    setPage(0);
  };

  const clearFilters = () => {
    setStage('');
    setSearch('');
    setSubmittedSearch('');
    setPage(0);
  };

  return (
    <StudioShell>
      <StudioHero
        eyebrow="StrymHub Studios"
        title="Every production,"
        accent="start to finish."
        lede="Not a category grid. Each production has its own page with the full cast list, the crew who made it, the trailer, the behind-the-scenes and the finished work — all in one place."
        stats={heroStats}
      />

      <div className={`${CONTAINER} py-12`}>
        <form onSubmit={handleSearchSubmit} className="mb-8">
          <label htmlFor="production-search" className="sr-only">
            Search productions
          </label>
          <div className="relative max-w-lg">
            <Search
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500"
              aria-hidden="true"
            />
            <input
              id="production-search"
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by title, logline or genre"
              className={`${INPUT} pl-11`}
            />
          </div>
        </form>

        <FilterChips
          label="Filter productions by stage"
          options={STAGE_FILTERS}
          value={stage}
          onChange={handleStageChange}
        />

        <div className="mt-10">
          {productions.loading && <StudioSkeleton count={8} />}

          {!productions.loading && productions.error && (
            <StudioErrorState message={productions.error} onRetry={productions.reload} />
          )}

          {!productions.loading && !productions.error && results && results.content.length === 0 && (
            <StudioEmptyState
              icon={<Clapperboard className="h-8 w-8" />}
              title={isFiltered ? 'Nothing matches that yet' : 'The slate is being built'}
              description={
                isFiltered
                  ? 'Try a different stage, or clear the filters to see the whole slate.'
                  : 'Our first productions are in development. Join the troupe and you could be in them.'
              }
              action={
                isFiltered ? (
                  <button type="button" onClick={clearFilters} className={SECONDARY_BUTTON}>
                    Clear filters
                  </button>
                ) : (
                  <Link to="/community/join" className={PRIMARY_BUTTON}>
                    Join the troupe
                  </Link>
                )
              }
            />
          )}

          {!productions.loading && !productions.error && results && results.content.length > 0 && (
            <>
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
                {results.content.map((production) => (
                  <ProductionCard key={production.id} production={production} />
                ))}
              </div>

              {results.totalPages > 1 && (
                <nav
                  aria-label="Productions pagination"
                  className="mt-12 flex items-center justify-center gap-4"
                >
                  <button
                    type="button"
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                    disabled={results.first}
                    className={SECONDARY_BUTTON}
                  >
                    Previous
                  </button>
                  <p aria-live="polite" className="text-sm text-neutral-400">
                    Page {results.number + 1} of {results.totalPages}
                  </p>
                  <button
                    type="button"
                    onClick={() => setPage((current) => current + 1)}
                    disabled={results.last}
                    className={SECONDARY_BUTTON}
                  >
                    Next
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </div>
    </StudioShell>
  );
};

export default ProductionsPage;
